import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { Api } from '../typings/api';
import RoutesUtils from './routes';
import UserAgentUtils from './userAgent';
import Utils from './';
import { AuthenticationUserState, Security, ConnectionStatus } from '../containers/Authentication/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { Server, ASP_SERVERS } from '../containers/MultiAppAuthentication/constants';
import DriveItemUtils from './driveItem';

class UrlUtils {
	// ----- URL Generators
	// returns a the result of a final URL function
	public static getUrlAuthentication(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathAuthentication(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}


	public static getUrlHome(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathHome(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlParameters(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathParameters(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemListPage(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemListPage(origin, originParam, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemDetailPage(galleryConfig: Api.Config, driveItemId: number, driveFolderId: number|undefined, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemDetailPage(driveItemId, driveFolderId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlTheater(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, driveItemId: number, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathTheater(origin, originParam, driveItemId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getApiGalleryUrl(galleryConfig: Api.Config, api: string) {
		return this.getApiItemUrl(galleryConfig, galleryConfig.applicationGallery, api);
	}

	public static getChildrenUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_children.json?token=${galleryConfig.service ? galleryConfig.service.token : ''}`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_meta.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getContentBlockUrl(galleryConfig: Api.Config, driveItemId: number, block: number) {
		const api = `_content-block-${block}.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getResourceZipUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_resource.zip`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getOutcomingsUrl = (galleryConfig: Api.Config, driveItemId: number): string => {
		const { service } = galleryConfig;
		let token = service ? `?token=${service.token}` : '';
		const api = `_outcomings.json${token}`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getAttachmentUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_attachment`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getNewContentsUrl(galleryConfig: Api.Config, sinceDate: Date) {
		const api = `_new.json?sinceDate=${sinceDate.getTime()}`;

		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getSearchUrl(galleryConfig: Api.Config, searchString: string) {
		const api = `_search.json?expr=${encodeURIComponent(searchString)}`;

		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getWelcomePageUrl = (galleryInfos: GalleryInfosState): string => {
		const { template, config } = galleryInfos;
		if (template && template.welcomepage && template.welcomepage.url) {
			return UrlUtils.getItemUrl(config, Utils.getMediaId(template.welcomepage.url));
		}
		return '';
	}

	public static getTemplateUrl(galleryConfig: Api.Config, server?: Server) {
		const fileName = `template.json`;
		if (server) {
			const driveUrl = ASP_SERVERS[server].driveUrl;
			const templateUrl = `${driveUrl}${galleryConfig.clientId}/${galleryConfig.applicationGallery}/.gallery/config/${fileName}`;

			return UrlUtils.addCredentialsIfNeeded(templateUrl);
		}

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getConfigUrl(server: Server, clientId: number, galleryId: number): string {
		const driveUrl = ASP_SERVERS[server].driveUrl;
		const url = `${driveUrl}${clientId}/${galleryId}/.gallery/config/config.json`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getStatisticsUrl(galleryConfig: Api.Config) {
		const fileName = `statistics.xml`;

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getLoginJsonUrl(galleryConfig: Api.Config) {
		return `${this.getRootApiGallery(galleryConfig)}_login.json`;
	}

	public static getSamlAuthenticationPage(params: string) {
		return `https://migrationv3.zone-secure.net/drive/saml-login/${params}`;
	}

	public static getMessageInterceptorUrl(server: Server) {
		const driveUrl = ASP_SERVERS[server].driveUrl;
		return `${driveUrl}messageInterceptor.html`;
	}

	public static getSamlIframeRedirectionUrl(appConfig: Api.Config) {
		return `${this.getRootApiGallery(appConfig)}/#/home`;
	}

	// ----- Final URLs
	// complete URL with credential if needed

	// Don't add credential to this url, url shared to contacts
	public static getGallAppUrl(galleryConfig: Api.Config, hash: string) {
		return `${this.getRootApiGallery(galleryConfig)}/#${hash}`;
	}

	public static getLoginToAspUrl(server: Server): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}services/rest/loginWs/appLogin`;
	}

	public static getLogoutFromAspUrl(server: Server): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}services/rest/loginWs/logout`;
	}

	public static getResetAspPasswordUrl(server: Server): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}react-admin/#/forgot_password`;
	}

	public static getCreateDemoAccountUrl(server: Server): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}services/rest/membershipWs/createDemoAccount`;
	}

	public static getAuthMethodsUrl(server: Server): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}services/rest/loginWs/getAuthMethods`;
	}

	public static getOrgLoginUrl(server: Server, orgName?: string): string {
		const webService = ASP_SERVERS[server].url;
		return `${webService}services/rest/loginWs/orgLogin${orgName ? `?org=${encodeURIComponent(orgName)}` : ''}`;
	}

	public static getLoginUrl(galleryConfig: Api.Config, login: string, password: string, method: Security) {
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		const userInfos = {
			needCredentials: true,
			login,
			password,
			mode: {
				method,
				status: ConnectionStatus.CONNECTED
			}
		};
		return UrlUtils.addCredentialsIfNeeded(url, userInfos);
	}

	public static getResolutionsUrl(galleryConfig: Api.Config) {
		const url = `${this.getRootApiGallery(galleryConfig)}/.drive/resolutions.json`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getItemOriginalFileUrl(galleryConfig: Api.Config, originalFile: string) {
		const url = `${UrlUtils.getRootApiClient(galleryConfig)}/${originalFile}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getImageUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, size: Api.ResolutionSize, extension: string) {
		const url = `${this.getRootApiItem(galleryConfig, DriveItemUtils.getIconId(driveItem))}_${size.box ? 'box-' : ''}${size.width}x${size.height}.${extension}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getPublicationUrl(
		galleryConfig: Api.Config, driveItem: Api.DriveItem, page: number = 1, isForMobileApp: boolean = false,
		isShare: boolean = false, hideWelcomePage: boolean = false
	): string {
		let publicationUrlParams = this.getPublicationUrlParams(page, isForMobileApp, hideWelcomePage);

		const url = `${this.getRootApiItem(galleryConfig, driveItem.id)}/index.html${publicationUrlParams}`;

		if (isShare) {
			return url;
		}

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getPublicationUrlParams(page: number = 1, isForMobileApp: boolean = false, hideWelcomePage: boolean = false) {
		let publicationUrlParams = '?';

		if (page !== 1) {
			const startPageParam = `startPage=${page}&`;
			publicationUrlParams += startPageParam;
		}
		if (isForMobileApp) {
			const mobileAppParameters = 'isApp=true&backHome=true&';
			publicationUrlParams += mobileAppParameters;
		}
		if (hideWelcomePage) {
			const hideWelcomePageParam = 'initState=nowelcomepage';
			publicationUrlParams += hideWelcomePageParam;
		}

		return publicationUrlParams;
	}

	public static getPublicationCoverUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, pageIndex: number) {
		const rootUrl = UrlUtils.getRootApiClient(galleryConfig);
		const cover = driveItem.thumbsPath ? DriveItemUtils.getPublicationCover(driveItem.thumbsPath, pageIndex + 1) : '';
		const url = `${rootUrl}/${driveItem.id}/${cover}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}


	// Credentials not needed for this API, bug ?
	public static getResourcesGroupUrl = (galleryConfig: Api.Config, ids: number[]): string => {
		const { service, clientId } = galleryConfig;
		const parsedIds = JSON.stringify(ids);
		return service ? Utils.getUrlWithCorrectProtocol(`${service.host}/${service.download.service}?clientId=${clientId}&ids=${parsedIds}&token=${service.token}`) : '';
	}

	public static getShareServiceUrl = (galleryConfig: Api.Config): string => {
		const { applicationGallery } = galleryConfig;
		return `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${applicationGallery}_share`;
	}

	public static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number): string => {
		const { config } = galleryInfos;
		const url = UrlUtils.getRootApiItem(config, componentId) + '/index.html';
		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getRootApiClient(galleryConfig: Api.Config) {
		const { clientId } = galleryConfig;

		const isMaster = Utils.isMaster(galleryConfig);
		if (isMaster) {
			return this.getMasterRootApi(galleryConfig);
		}

		const isMobileApp = UserAgentUtils.isMobileApp;
		if (isMobileApp || process.env.NODE_ENV !== 'production') {
			return `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}`;
		}

		return `${this.getRelativeRootApi(galleryConfig)}/${clientId}`;
	}

	public static getConfiguredRootApiClient(galleryConfig: Api.Config) {
		const { clientId } = galleryConfig;
		return `${this.getConfiguredRootApi(galleryConfig)}/${clientId}`;
	}

	public static getRootApiGallery(galleryConfig: Api.Config) {

		const isMobileApp = UserAgentUtils.isMobileApp;
		if (isMobileApp) {
			const { applicationGallery } = galleryConfig;
			return this.getRootApiItem(galleryConfig, applicationGallery);
		}

		return `${window.location.origin}${window.location.pathname}`;
	}

	private static getItemUrl(galleryConfig: Api.Config, driveItemId: number) {
		const url = UrlUtils.getRootApiItem(galleryConfig, driveItemId);

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	private static getApiItemUrl(galleryConfig: Api.Config, driveItemId: number, api: string) {
		const url = `${UrlUtils.getRootApiItem(galleryConfig, driveItemId)}${api}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	private static getConfigFileUrl(galleryConfig: Api.Config, fileName: string) {
		const url = `${this.getRootApiGallery(galleryConfig)}/.gallery/config/${fileName}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	// ----- Root URLs
	// not used directly, Final Urls append things to it and add credentials if needed

	private static getRootApiItem(galleryConfig: Api.Config, driveItemId: number) {
		return `${this.getRootApiClient(galleryConfig)}/${driveItemId}`;
	}

	private static getMasterRootApi(galleryConfig: Api.Config): string {
		const { mediaPath } = galleryConfig;
		return `${mediaPath ? mediaPath : ''}`;
	}

	private static getConfiguredRootApi(galleryConfig: Api.Config): string {
		const { applicationUrl } = galleryConfig;
		return Utils.getUrlWithCorrectProtocol(applicationUrl);
	}

	private static getRelativeRootApi(galleryConfig: Api.Config): string {
		const { applicationUrl } = galleryConfig;

		// get the part before the /-/${customClientPath}/-/ or /${clientId}/${galleryId}
		const relativeRootApiRegex = /^(.*?)(\/[0-9]+\/[0-9]+\/?|\/-\/.*\/-)$/;
		const regexMatch = relativeRootApiRegex.exec(window.location.origin + window.location.pathname);
		const relativeRootApi = regexMatch ? regexMatch[1] : applicationUrl;

		return Utils.getUrlWithCorrectProtocol(relativeRootApi);
	}

	// ----- Helpers
	private static addCredentialsIfNeeded(url: string, userInfos?: AuthenticationUserState): string {
		const galleryCredentials = Utils.getGalleryCredentials(userInfos);
		const multiAppCredentials = Utils.getMultiAppCredentials();

		if (!multiAppCredentials && !galleryCredentials) {
			return url;
		}

		let login;
		let password;
		if (multiAppCredentials) {
			login = multiAppCredentials.login;
			password = multiAppCredentials.password;
		} else if (galleryCredentials) {
			login = galleryCredentials.login;
			password = galleryCredentials.password;
		}

		const includesSeparator = url.includes('?');
		const startingSeparator = includesSeparator ? '&' : '?';
		return url + (login && password ? `${startingSeparator}user=${encodeURIComponent(login)}&pass=${encodeURIComponent(password)}` : '');
	}
}

export const urlUtils = new UrlUtils;

export default UrlUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/url.ts